/**
 * Chat attachment size limits, shared by the browser and the API.
 *
 * A leaf module with no runtime dependencies so the app bundle can import it:
 * session-chat-uploads.ts pulls in the S3 clients and the pool, which cannot
 * cross into the browser.
 */

/**
 * Ceiling on a single chat attachment, enforced server-side by
 * confirmUploadedChatFile against the object's real size.
 *
 * The client checks the same number at pick time so an oversized file fails
 * before the upload rather than after it. Both sides read this constant: a
 * client limit above the server's rejects a file only after the user has
 * waited out the transfer, and one below it silently narrows the product.
 */
export const MAX_CHAT_UPLOAD_SIZE_BYTES = 1024 * 1024 * 1024;

/**
 * Ceiling on a single attachment while the worker still inlines attachment
 * bytes into the agent runtime payload. The browser imports this limit so both
 * admission paths use the same 50 MB request-body budget and base64 allowance.
 */
export const MAX_INLINE_ATTACHMENT_SIZE_BYTES = Math.floor(
  (50 * 1024 * 1024 - 2 * 1024 * 1024) / (4 / 3),
);

/**
 * Ceiling on a single context file, enforced server-side by
 * confirmUploadedContextFile against the object's real size.
 *
 * Decimal GB rather than GiB so the drop zone renders a clean "1 GB" rather
 * than "1.07 GB". The client checks the same number at pick time, for the same
 * reason MAX_CHAT_UPLOAD_SIZE_BYTES is shared.
 */
export const MAX_CONTEXT_FILE_SIZE_BYTES = 1_000_000_000;

/**
 * Attachments allowed on a single message.
 *
 * The client has always capped this for usability, but the server enforced no
 * count: verification HEADs every staged key concurrently, so a request naming
 * a thousand keys was a thousand concurrent S3 calls from one mutation.
 * Verification is now concurrency-bounded (see CHAT_UPLOAD_VERIFY_CONCURRENCY),
 * so this number is a per-message usability choice rather than a safety valve.
 */
export const MAX_CHAT_UPLOAD_FILES = 100;

/** Attachments allowed on a single context agent message, where a folder drop becomes asset library uploads. */
export const MAX_CONTEXT_AGENT_UPLOAD_FILES = 500;

/** Attachments allowed on a single message to a Pi agent. MAX_CHAT_UPLOAD_TOTAL_BYTES still caps their total size. */
export const MAX_AGENT_UPLOAD_FILES = 500;

/**
 * How many staged keys are verified at once. Verification is one HEAD per key;
 * unbounded, a large attachment set becomes that many simultaneous S3 calls
 * from a single mutation.
 */
export const CHAT_UPLOAD_VERIFY_CONCURRENCY = 10;

/** Total attachment bytes accepted on one chat message. */
export const MAX_CHAT_UPLOAD_TOTAL_BYTES = 2 * 1024 * 1024 * 1024;
